import {
  type ApolloClient,
  ApolloError,
  type ApolloQueryResult,
  NetworkStatus,
} from "@apollo/client";
import { useAnalyticsApolloClient } from "@package/ui-apollo-components";
import {
  GetEventStatsDocument,
  type GetEventStatsQuery,
  type GetEventStatsQueryVariables,
} from "@package/ui-gql/index-analytics";
import { useEffect, useState } from "react";

type UseGetEventStatsResponse = ApolloQueryResult<GetEventStatsQuery> & {
  refetch: (
    refetchVariables?: GetEventStatsQueryVariables,
  ) => Promise<ApolloQueryResult<GetEventStatsQuery>>;
};

const defaultQueryResult: ApolloQueryResult<GetEventStatsQuery> = {
  data: {
    item: {
      __typename: "EventStats",
      avgInteractionSeconds: undefined,
      contentOpenedCount: 0,
      dailyTrend: [],
      proofOfPlayCount: 0,
      qrScansCount: 0,
      topContent: [],
    },
  },
  loading: false,
  networkStatus: NetworkStatus.ready,
};

export function useGetEventStats(
  variables: GetEventStatsQueryVariables,
  skip = false,
  apolloClient?: ApolloClient<object>,
) {
  const client = useAnalyticsApolloClient(apolloClient);
  const [response, setResponse] = useState<UseGetEventStatsResponse>({
    ...defaultQueryResult,
    refetch: async () => defaultQueryResult,
  });

  useEffect(
    function effectOnFetchEventStats() {
      const fetchEventStats = async (
        variablesOverride?: GetEventStatsQueryVariables,
      ) => {
        try {
          const r = await client?.query<
            GetEventStatsQuery,
            GetEventStatsQueryVariables
          >({
            query: GetEventStatsDocument,
            variables: variablesOverride ?? variables,
          });
          if (r) {
            setResponse({
              ...r,
              refetch: async (refetchVariables?: GetEventStatsQueryVariables) =>
                (await fetchEventStats(refetchVariables)) ?? defaultQueryResult,
            });
          }

          return r;
        } catch (error: unknown) {
          console.error("Error fetching event stats:", error);
          setResponse((prev) => ({
            ...prev,
            error: new ApolloError({
              errorMessage: "Error fetching event stats",
            }),
            loading: false,
          }));
        }
      };

      if (!skip) {
        fetchEventStats();
      }
    },
    [client, skip, variables],
  );

  return response;
}
